import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import api from "../../api";
import { apiErrorMessage } from '../../utils/apiError';

const UpdateSessionNotesModal = ({ isOpen, onClose, session, onSuccess, role = "ADMIN" }) => {
    const { t } = useTranslation();
    const [sessionNotes, setSessionNotes] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (isOpen && session) {
            setSessionNotes(session.sessionNotes || "");
            setError("");
        }
    }, [isOpen, session]);

    const handleSave = async () => {
        if (!session) return;

        setSaving(true);
        setError("");

        const base =
            String(role).toUpperCase() === "MANAGER" ? "manager"
                : String(role).toUpperCase() === "COACH" ? "coach"
                    : "admin";

        try {
            await api.patch(`/${base}/sessions/${session.sessionId}/notes`, {
                sessionNotes: sessionNotes
            });

            onSuccess?.();
            onClose();
        } catch (err) {
            console.error("Failed to update session notes:", err);
            setError(apiErrorMessage(err, t('sessions.updateNotesModal.updateFailed')));
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen || !session) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-2xl font-bold text-white">{t('sessions.updateNotesModal.title')}</h2>
                            <p className="text-indigo-100 text-sm mt-1">
                                {t('sessions.updateNotesModal.subtitle', {
                                    sessionId: session.sessionId,
                                    batchName: session.batchName,
                                })}
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
                            disabled={saving}
                            type="button"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                <div className="p-6">
                    {error && (
                        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-red-800 text-sm">{error}</p>
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('sessions.updateNotesModal.sessionNotes')}
                        </label>
                        <textarea
                            className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent min-h-[200px] resize-y"
                            value={sessionNotes}
                            onChange={(e) => setSessionNotes(e.target.value)}
                            placeholder={t('sessions.updateNotesModal.placeholder')}
                            maxLength={2000}
                            disabled={saving}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            {t('sessions.updateNotesModal.charCount', { count: sessionNotes.length })}
                        </p>
                    </div>
                </div>

                <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t border-gray-200">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-100 transition-colors"
                        disabled={saving}
                        type="button"
                    >
                        {t('common.cancel')}
                    </button>
                    <button
                        onClick={handleSave}
                        className="px-4 py-2 rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        disabled={saving}
                        type="button"
                    >
                        {saving ? t('sessions.updateNotesModal.saving') : t('sessions.updateNotesModal.saveNotes')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UpdateSessionNotesModal;
